import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { PokerSiteId } from '@poker-stats/contracts';
import type { MiningUploadItem, StartMiningUploadPayload } from './types';

type MiningUploadState = {
  queueId?: string;
  batchId?: string;
  room: PokerSiteId;
  items: MiningUploadItem[];
  skippedCount: number;
  isUploading: boolean;
};

const initialState: MiningUploadState = {
  room: 1,
  items: [],
  skippedCount: 0,
  isUploading: false,
};

export const miningUploadReducer = createSlice({
  name: 'miningUpload',
  initialState,
  reducers: {
    initializeMiningQueue: (_state, action: PayloadAction<string>) => ({
      ...initialState,
      queueId: action.payload,
    }),
    changeMiningRoom(state, action: PayloadAction<{ queueId: string; room: PokerSiteId }>) {
      if (state.queueId !== action.payload.queueId || state.items.length || state.isUploading)
        return;
      state.room = action.payload.room;
      state.skippedCount = 0;
    },
    addMiningItems(
      state,
      action: PayloadAction<{
        queueId: string;
        items: MiningUploadItem[];
        skippedCount: number;
      }>,
    ) {
      if (state.queueId !== action.payload.queueId || state.isUploading) return;
      state.items.push(...action.payload.items);
      state.skippedCount += action.payload.skippedCount;
    },
    startMiningUpload(state, action: PayloadAction<StartMiningUploadPayload>) {
      if (
        state.queueId !== action.payload.queueId ||
        state.isUploading ||
        state.room !== action.payload.room ||
        !action.payload.upload.files.length
      )
        return;
      state.batchId = action.payload.batchId;
      state.isUploading = true;
      const selected = new Set(action.payload.upload.files.map(({ id }) => id));
      for (const item of state.items) {
        if (selected.has(item.id) && item.status !== 'uploaded') {
          item.status = 'pending';
          delete item.error;
        }
      }
    },
    setMiningItemStatus(
      state,
      action: PayloadAction<{
        queueId: string;
        batchId: string;
        id: string;
        status: MiningUploadItem['status'];
        error?: string;
      }>,
    ) {
      if (
        state.queueId !== action.payload.queueId ||
        state.batchId !== action.payload.batchId ||
        !state.isUploading
      )
        return;
      const item = state.items.find(({ id }) => id === action.payload.id);
      if (!item) return;
      item.status = action.payload.status;
      item.error = action.payload.error;
    },
    finishMiningUpload(state, action: PayloadAction<{ queueId: string; batchId: string }>) {
      if (state.queueId !== action.payload.queueId || state.batchId !== action.payload.batchId)
        return;
      state.isUploading = false;
      state.batchId = undefined;
    },
    clearMiningQueue(state, action: PayloadAction<string>) {
      if (state.queueId !== action.payload || state.isUploading) return;
      state.items = [];
      state.skippedCount = 0;
    },
    disposeMiningQueue(state, action: PayloadAction<string>) {
      if (state.queueId !== action.payload) return;
      return initialState;
    },
  },
});

export const {
  initializeMiningQueue,
  changeMiningRoom,
  addMiningItems,
  startMiningUpload,
  setMiningItemStatus,
  finishMiningUpload,
  clearMiningQueue,
  disposeMiningQueue,
} = miningUploadReducer.actions;
